import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Loader2, AlertCircle, Mail, Paperclip, Star, RefreshCw, GripVertical, MoreHorizontal, ChevronDown, ChevronUp, CheckCircle, Circle, Brain, CheckSquare, Square, Plus } from 'lucide-react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { mailApi, todoApi, subtasksApi, MailTodoLink } from '../../api/client';
import { formatDate } from '../../utils/date';
import { cn } from '../../utils/cn';
import { SubtaskList } from './SubtaskList';

interface KanbanColumnProps {
  column: any;
  account: any;
  onSelectMail: (messageId: string) => void;
  selectedMessageId?: string;
  isFrozen?: boolean;
  isDragEnabled?: boolean;
  onQuickAction?: (mailData: { message: any; accountId: string; columnId: string }) => void;
  onAddTodo?: (mailData: { message: any; accountId: string }) => void;
  excludeMessageIds?: Set<string>;
  onMessagesLoaded?: (messageIds: string[]) => void;
  showOnlyWithTasks?: boolean; // Punkt 1: Task-Filter
  hiddenCategoryNames?: string[]; // Categories from hidden columns - mails with these should be filtered from folders
  showSummary?: boolean; // Kerninhalt-Modus: KI-Zusammenfassung statt Preview
  onToggleSummary?: () => void; // Toggle-Handler
  showCompletionPopup?: boolean; // Show action popup when completing a main task
  viewMode?: 'board' | 'list';
}

export default function KanbanColumn({ 
  column, 
  account, 
  onSelectMail,
  selectedMessageId,
  isFrozen = false,
  isDragEnabled = true,
  onQuickAction,
  onAddTodo,
  excludeMessageIds,
  onMessagesLoaded,
  showOnlyWithTasks = false,
  hiddenCategoryNames = [],
  showSummary = false,
  onToggleSummary,
  showCompletionPopup = true,
  viewMode = 'board',
}: KanbanColumnProps) {
  const [loadedMessages, setLoadedMessages] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMoreToLoad, setHasMoreToLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  
  // Punkt 5: Initial messages fetch
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['messages', account?.id, column.sourceId, column.baseFolderId, 0], // Initial offset 0
    queryFn: () => {
      if (!account) return { messages: [], hasMore: false };
      
      const params: any = { limit: 50, offset: 0 };
      if (column.sourceType === 'folder') {
        params.folderId = column.sourceId;
      } else if (column.sourceType === 'label' || column.sourceType === 'category') {
        params.labelId = column.sourceId;
        // Apply base folder filter for category columns (scope to specific folder)
        if (column.baseFolderId) {
          params.baseFolderId = column.baseFolderId;
        }
      }
      
      return mailApi.getMessages(account.id, params);
    },
    enabled: !!account,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
  
  // Update loaded messages when initial data changes
  useEffect(() => {
    if (data?.messages) {
      setLoadedMessages(data.messages);
      setHasMoreToLoad(data.hasMore);
      setOffset(data.messages.length);
    }
  }, [data]);
  
  // Punkt 5: Load more messages (infinite scroll)
  const loadMore = useCallback(async () => {
    if (!account || isLoadingMore || !hasMoreToLoad) return;
    
    setIsLoadingMore(true);
    try {
      const params: any = { limit: 50, offset };
      if (column.sourceType === 'folder') {
        params.folderId = column.sourceId;
      } else if (column.sourceType === 'label' || column.sourceType === 'category') {
        params.labelId = column.sourceId;
        // Apply base folder filter for category columns
        if (column.baseFolderId) {
          params.baseFolderId = column.baseFolderId;
        }
      }
      
      const result = await mailApi.getMessages(account.id, params);
      setLoadedMessages(prev => [...prev, ...result.messages]);
      setHasMoreToLoad(result.hasMore);
      setOffset(prev => prev + result.messages.length);
    } catch (err) {
      console.error('Failed to load more messages:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [account, column, offset, isLoadingMore, hasMoreToLoad]);
  
  // Punkt 5: Scroll handler for infinite scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    // Load more when scrolled near bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100 && hasMoreToLoad && !isLoadingMore) {
      loadMore();
    }
  }, [loadMore, hasMoreToLoad, isLoadingMore]);
  
  // Use loadedMessages (includes paginated results)
  const rawMessages = loadedMessages;
  const messageIds = useMemo(() => rawMessages.map((m: any) => m.id), [rawMessages]);
  
  // Fetch todo links for all messages (mail converted to external task)
  const { data: todoLinksData } = useQuery({
    queryKey: ['mailTodoLinks', account?.id, messageIds],
    queryFn: () => {
      if (!account || messageIds.length === 0) return { links: {} };
      return todoApi.getMailLinksBatch(account.id, messageIds);
    },
    enabled: !!account && messageIds.length > 0,
    staleTime: 10 * 1000, // Reduced: 10 seconds cache
    refetchInterval: 15 * 1000, // Sync every 15 seconds
  });
  
  const todoLinks = todoLinksData?.links || {};
  
  // Track if we're generating summaries progressively
  const [generatingBatch, setGeneratingBatch] = useState(false);
  
  // Fetch AI summaries when showSummary is enabled
  const { data: summariesData, isLoading: isSummariesLoading, refetch: refetchSummaries } = useQuery({
    queryKey: ['mailSummaries', account?.id, messageIds, showSummary],
    queryFn: async () => {
      if (!account || messageIds.length === 0) return { summaries: {}, missingCount: 0 };
      // First check cache
      const cached = await mailApi.getSummaries(account.id, messageIds);
      const missing = messageIds.filter((id: string) => !cached.summaries[id]);
      
      if (missing.length > 0) {
        // Generate missing summaries in batch of 10
        const toGenerate = rawMessages
          .filter((m: any) => missing.includes(m.id))
          .slice(0, 10)
          .map((m: any) => ({
            messageId: m.id,
            subject: m.subject || '',
            bodyPreview: m.bodyPreview || '',
            from: m.from?.name || m.from?.email || '',
          }));
        
        if (toGenerate.length > 0) {
          const generated = await mailApi.generateSummariesBatch(account.id, toGenerate);
          return { 
            summaries: { ...cached.summaries, ...generated.summaries },
            missingCount: missing.length - toGenerate.length // Track remaining
          };
        }
      }
      
      return { summaries: cached.summaries, missingCount: 0 };
    },
    enabled: !!account && messageIds.length > 0 && showSummary,
    staleTime: 15 * 60 * 1000, // 15 min - summaries are persisted in DB
  });
  
  const summaries = summariesData?.summaries || {};
  const remainingSummaries = summariesData?.missingCount || 0;
  
  // Auto-generate remaining summaries in batches
  useEffect(() => {
    if (showSummary && remainingSummaries > 0 && !isSummariesLoading && !generatingBatch) {
      const timer = setTimeout(() => {
        setGeneratingBatch(true);
        refetchSummaries().finally(() => setGeneratingBatch(false));
      }, 500); // Small delay between batches
      return () => clearTimeout(timer);
    }
  }, [showSummary, remainingSummaries, isSummariesLoading, generatingBatch, refetchSummaries]);
  
  // Filter messages: duplicates + hidden categories + task filter
  const messages = useMemo(() => {
    let filtered = rawMessages;
    
    // DEBUG: Log raw vs filtered
    // Debug removed for production
    
    // Filter duplicates (by ID)
    if (excludeMessageIds && excludeMessageIds.size > 0) {
      const before = filtered.length;
      filtered = filtered.filter((m: any) => !excludeMessageIds.has(m.id));
      // Debug removed for production
    }
    
    // Filter mails that have a hidden category (e.g. "Erledigt" column is hidden)
    // Only applies to folder columns - category columns show their own mails
    if (hiddenCategoryNames.length > 0 && column.sourceType === 'folder') {
      const before = filtered.length;
      filtered = filtered.filter((m: any) => {
        const mailCategories: string[] = m.categories || [];
        // Keep message if it has NONE of the hidden categories
        return !mailCategories.some(cat => hiddenCategoryNames.includes(cat));
      });
      // Debug removed for production
    }
    
    // Punkt 1: Filter by tasks (Mail converted to Microsoft To-Do)
    if (showOnlyWithTasks) {
      filtered = filtered.filter((m: any) => {
        const todoLink = todoLinks[m.id];
        // Mail is converted to Microsoft To-Do task
        return !!todoLink;
      });
    }
    
    // Sort by receivedAt descending (newest first)
    filtered = [...filtered].sort((a: any, b: any) => {
      const dateA = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
      const dateB = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
      return dateB - dateA;
    });
    
    return filtered;
  }, [rawMessages, excludeMessageIds, hiddenCategoryNames, column.sourceType, showOnlyWithTasks, todoLinks, column.displayName]);
  
  // Report loaded message IDs to parent for duplicate tracking
  useEffect(() => {
    if (onMessagesLoaded && messageIds.length > 0) {
      onMessagesLoaded(messageIds);
    }
  }, [messageIds, onMessagesLoaded]);
  
  // Setup as drop target
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `column-${column.id}`,
    data: {
      type: 'column',
      column,
      accountId: account?.id,
    },
  });
  
  // Hide column if hideWhenEmpty is true and no messages (after loading)
  if (column.hideWhenEmpty && !isLoading && messages.length === 0) {
    return null;
  }
  
  return (
    <div 
      ref={setDropRef}
      className={cn(
        cn('flex flex-col overflow-hidden transition-all', viewMode === 'list' ? 'flex-1 min-h-0' : 'flex-shrink-0 rounded-xl shadow-theme'),
        viewMode === 'list' ? 'w-full' : 'w-80',
        isOver && 'ring-2 ring-primary ring-offset-2'
      )}
      style={{
        backgroundColor: isOver ? 'var(--bg-hover)' : 'var(--bg-primary)',
        border: `1px solid ${isOver ? 'var(--brand-primary)' : 'var(--border-default)'}`,
        maxHeight: viewMode === 'list' ? '100%' : 'calc(100vh - 180px)',
        minHeight: '200px',
        height: viewMode === 'list' ? '100%' : 'fit-content',
        ...(isFrozen ? {
          position: 'sticky',
          left: 0,
          zIndex: 10,
          boxShadow: '4px 0 12px rgba(0,0,0,0.15)',
        } : {}),
      }}
    >
      {/* Column Header */}
      <div 
        className="flex items-center justify-between px-4 py-3"
        style={{
          borderBottom: '1px solid var(--border-default)',
          backgroundColor: isFrozen ? 'var(--brand-primary)' : 'var(--bg-secondary)',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{column.icon || '📂'}</span>
          <h3 
            className="font-semibold"
            style={{ color: isFrozen ? 'white' : 'var(--text-primary)' }}
          >
            {column.displayName}
          </h3>
          <span 
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ 
              backgroundColor: isFrozen ? 'rgba(255,255,255,0.2)' : 'var(--bg-tertiary)',
              color: isFrozen ? 'white' : 'var(--text-secondary)',
            }}
            title={`${messages.length} geladen${hasMoreToLoad ? ', mehr verfügbar' : ''}`}
          >
            {/* Punkt 5: Show loaded/total count */}
            {hasMoreToLoad ? `${messages.length}+` : messages.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Kerninhalt Toggle */}
          {onToggleSummary && (
            <button 
              onClick={onToggleSummary}
              className={cn(
                "p-1.5 rounded-lg transition-colors flex items-center gap-1",
                showSummary && "bg-purple-500/20"
              )}
              style={{ color: showSummary ? 'rgb(168, 85, 247)' : (isFrozen ? 'white' : 'var(--text-tertiary)') }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = showSummary ? 'rgba(168, 85, 247, 0.3)' : (isFrozen ? 'rgba(255,255,255,0.1)' : 'var(--bg-hover)')}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = showSummary ? 'rgba(168, 85, 247, 0.2)' : 'transparent'}
              title={showSummary 
                ? (remainingSummaries > 0 ? `Kerninhalt (KI) - ${remainingSummaries} werden generiert...` : 'Kerninhalt (KI) aktiv') 
                : 'Kerninhalt (KI) aktivieren'}
            >
              <Brain className="w-4 h-4" />
              {(isSummariesLoading || generatingBatch) && showSummary && <Loader2 className="w-3 h-3 animate-spin" />}
            </button>
          )}
          <button 
            onClick={() => refetch()}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: isFrozen ? 'white' : 'var(--text-tertiary)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = isFrozen ? 'rgba(255,255,255,0.1)' : 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Messages List - Punkt 5: Infinite Scroll */}
      <div 
        ref={listRef}
        className={cn(
          'flex-1 overflow-y-auto',
          viewMode === 'list' ? 'p-0' : 'p-2 space-y-2'
        )}
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-default)', padding: '0.75rem' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="h-3.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '40%' }} />
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '15%' }} />
                </div>
                <div className="h-3.5 rounded mb-2" style={{ backgroundColor: 'var(--bg-tertiary)', width: '80%' }} />
                <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '60%' }} />
                <div className="flex gap-2 mt-3">
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '20%' }} />
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '15%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="w-5 h-5 text-error mb-2" />
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Fehler beim Laden</p>
            <button 
              onClick={() => refetch()}
              className="mt-2 text-xs text-primary hover:underline"
            >
              Erneut versuchen
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Mail className="w-8 h-8 mb-2" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Keine E-Mails</p>
          </div>
        ) : (
          <>
            {messages.map((message: any) => (
              <DraggableMailCard
                key={message.id}
                message={message}
                accountId={account?.id}
                columnId={column.id}
                sourceId={column.sourceId}
                isSelected={selectedMessageId === message.id}
                onClick={() => onSelectMail(message.id)}
                isDragEnabled={isDragEnabled}
                onQuickAction={onQuickAction}
                onAddTodo={onAddTodo}
                onTaskComplete={onQuickAction}
                todoLink={todoLinks[message.id]}
                provider={account?.provider || 'microsoft'}
                summary={showSummary ? summaries[message.id] : undefined}
                showCompletionPopup={showCompletionPopup}
                viewMode={viewMode}
              />
            ))}
            {/* Punkt 5: Loading more indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                <span className="ml-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>Lade mehr...</span>
              </div>
            )}
            {hasMoreToLoad && !isLoadingMore && (
              <button
                onClick={loadMore}
                className="w-full py-2 text-xs transition-colors rounded-lg"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Mehr laden...
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface DraggableMailCardProps {
  message: any;
  accountId: string;
  columnId: string;
  sourceId: string;
  isSelected: boolean;
  onClick: () => void;
  isDragEnabled: boolean;
  onQuickAction?: (mailData: { message: any; accountId: string; columnId: string }) => void;
  onAddTodo?: (mailData: { message: any; accountId: string }) => void;
  onTaskComplete?: (mailData: { message: any; accountId: string; columnId: string }) => void;
  todoLink?: MailTodoLink;
  provider: string;
  summary?: string; // AI-generated summary (Kerninhalt)
  showCompletionPopup?: boolean; // Show action popup when completing a main task
  viewMode?: 'board' | 'list';
}

function DraggableMailCard({ 
  message, 
  accountId, 
  columnId, 
  sourceId,
  isSelected, 
  onClick,
  isDragEnabled,
  onQuickAction,
  onAddTodo,
  onTaskComplete,
  todoLink,
  provider,
  summary,
  showCompletionPopup = true,
  viewMode = 'board',
}: DraggableMailCardProps) {
  // Punkt 6: Aufgaben nur ausklappen wenn tatsächlich Subtasks existieren
  const [showTasks, setShowTasks] = useState(false);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch subtasks count to determine if we should auto-expand
  const { data: subtasksData } = useQuery({
    queryKey: ['subtasks', accountId, message.id],
    queryFn: () => subtasksApi.getSubtasks(accountId, message.id),
    enabled: provider === 'microsoft' && !!todoLink,
    staleTime: 10 * 1000,
  });
  
  const subtasksCount = subtasksData?.subtasks?.length || 0;
  
  // Auto-expand only when there are actual subtasks
  useEffect(() => {
    if (todoLink && !hasAutoExpanded && subtasksCount > 0) {
      setShowTasks(true);
      setHasAutoExpanded(true);
    }
  }, [todoLink, hasAutoExpanded, subtasksCount]);
  
  // Toggle todo completion mutation
  const toggleTodoMutation = useMutation({
    mutationFn: () => todoApi.toggleMailTodo(accountId, message.id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mailTodoLink', accountId, message.id] });
      queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
      
      // If task was just completed AND showCompletionPopup is enabled, trigger action popup
      if (data.link.completed && showCompletionPopup && onTaskComplete) {
        onTaskComplete({ message, accountId, columnId });
      }
    },
  });
  
  const handleToggleTodo = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleTodoMutation.mutate();
  };
  
  const handleAddTodo = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddTodo) {
      onAddTodo({ message, accountId });
    }
  };
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `mail-${message.id}`,
    data: {
      type: 'mail',
      message,
      accountId,
      columnId,
      sourceId,
    },
    disabled: !isDragEnabled,
  });
  
  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    zIndex: 1000,
    opacity: isDragging ? 0.8 : 1,
  } : undefined;
  
  const handleToggleTasks = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTasks(!showTasks);
  };
  
  // ===== LIST MODE: Compact Outlook/Gmail-style row =====
  if (viewMode === 'list') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'w-full text-left transition-colors cursor-pointer group',
          isSelected && 'ring-1 ring-primary',
          isDragging && 'opacity-60'
        )}
        onClick={onClick}
        {...attributes}
      >
        <div 
          className="flex items-start gap-1"
          style={{
            backgroundColor: isSelected ? 'var(--bg-hover)' : 'transparent',
            borderBottom: '1px solid var(--border-default)',
            borderLeft: message.importance === 'high' 
              ? '3px solid var(--color-error)' 
              : message.importance === 'low'
                ? '3px solid var(--text-disabled)'
                : '3px solid transparent',
            padding: '0.375rem 0.75rem',
          }}
          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          {/* Minimal drag handle */}
          {isDragEnabled && (
            <div 
              {...listeners}
              className="hidden sm:flex flex-shrink-0 items-center pt-1 cursor-grab active:cursor-grabbing opacity-0 hover:opacity-100 transition-opacity"
              onClick={e => e.stopPropagation()}
            >
              <GripVertical className="w-3 h-3" style={{ color: 'var(--text-disabled)' }} />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            {/* Row 1: Sender ......... Date */}
            <div className="flex items-center justify-between gap-2">
              <span 
                className={cn('text-sm truncate', !message.isRead && 'font-semibold')}
                style={{ color: 'var(--text-primary)' }}
              >
                {message.from.name || message.from.email}
              </span>
              <span className="text-xs whitespace-nowrap flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                {formatDate(message.receivedAt)}
              </span>
            </div>
            
            {/* Row 2: Subject [📎] [⭐] [Badges] */}
            <div className="flex items-center gap-1.5">
              <span 
                className={cn('text-sm truncate flex-1 min-w-0', !message.isRead && 'font-semibold')}
                style={{ color: 'var(--text-primary)' }}
              >
                {message.subject}
              </span>
              {message.hasAttachments && (
                <Paperclip className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
              )}
              {message.importance === 'high' && (
                <Star className="w-3 h-3 flex-shrink-0 text-warning fill-warning" />
              )}
              {todoLink && (
                <button
                  onClick={handleToggleTodo}
                  disabled={toggleTodoMutation.isPending}
                  className="inline-flex items-center gap-0.5 text-[10px] px-1 py-0 rounded font-medium flex-shrink-0"
                  style={{
                    backgroundColor: todoLink.completed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                    color: todoLink.completed ? 'var(--color-success)' : 'var(--color-warning)',
                  }}
                >
                  {todoLink.completed ? <CheckCircle className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
                </button>
              )}
              {message.categories?.slice(0, 2).map((cat: string, i: number) => (
                <span
                  key={i}
                  className="text-[10px] px-1 py-0 rounded font-medium flex-shrink-0 truncate max-w-[80px]"
                  style={{ backgroundColor: 'rgba(99, 102, 241, 0.15)', color: 'var(--brand-primary)' }}
                >
                  {cat}
                </span>
              ))}
            </div>
            
            {/* Row 3: Preview / AI Summary */}
            {summary ? (
              <div 
                className="text-xs truncate mt-0.5 px-1.5 py-0.5 rounded"
                style={{ 
                  color: 'rgb(139, 92, 246)',
                  backgroundColor: 'rgba(139, 92, 246, 0.08)',
                  borderLeft: '2px solid rgb(139, 92, 246)',
                }}
              >
                <span>✨ {summary}</span>
              </div>
            ) : message.bodyPreview ? (
              <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                {message.bodyPreview}
              </p>
            ) : null}
          </div>
          
          {/* Quick action */}
          {onQuickAction && (
            <button
              className="flex-shrink-0 p-1 rounded transition-opacity opacity-0 group-hover:opacity-100"
              style={{ color: 'var(--text-tertiary)' }}
              onClick={e => { e.stopPropagation(); onQuickAction({ message, accountId, columnId }); }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.opacity = '0'; }}
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ===== BOARD MODE: Original card layout =====
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'w-full text-left transition-all cursor-pointer p-3 rounded-lg',
        isSelected && 'ring-2 ring-primary',
        isDragging && 'shadow-2xl'
      )}
      onClick={onClick}
      {...attributes}
    >
      <div 
        className="flex items-start gap-2"
        style={{
          backgroundColor: isSelected 
            ? 'var(--bg-hover)' 
            : message.isRead 
              ? 'var(--bg-primary)' 
              : 'rgba(99, 102, 241, 0.08)',
          border: `1px solid ${isSelected ? 'var(--brand-primary)' : 'var(--border-default)'}`,
          borderLeft: message.importance === 'high' 
            ? '3px solid var(--color-error)' 
            : message.importance === 'low'
              ? '3px solid var(--text-disabled)'
              : '3px solid transparent',
          borderRadius: '0.5rem',
          padding: '0.75rem',
        }}
      >
        {/* Drag Handle (desktop) + Quick Action Button (mobile) */}
        {isDragEnabled && (
          <div className="flex-shrink-0 flex flex-col gap-1 -ml-1">
            {/* Drag handle - hidden on touch devices */}
            <div 
              {...listeners}
              className="hidden sm:block cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-black/5"
              onClick={e => e.stopPropagation()}
            >
              <GripVertical className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />
            </div>
            
            {/* Quick action button - shown on mobile, also available on desktop */}
            {onQuickAction && (
              <button
                className="p-1.5 rounded-lg transition-colors"
                style={{ 
                  color: 'var(--text-tertiary)',
                  backgroundColor: 'transparent',
                }}
                onClick={e => {
                  e.stopPropagation();
                  onQuickAction({ message, accountId, columnId });
                }}
                onTouchStart={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onTouchEnd={e => e.currentTarget.style.backgroundColor = 'transparent'}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Verschieben..."
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          {/* Sender & Date */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <span 
              className={cn(
                'text-sm truncate',
                !message.isRead && 'font-semibold'
              )}
              style={{ color: 'var(--text-primary)' }}
            >
              {message.from.name || message.from.email}
            </span>
            <span 
              className="text-xs whitespace-nowrap"
              style={{ color: 'var(--text-tertiary)' }}
            >
              {formatDate(message.receivedAt)}
            </span>
          </div>
          
          {/* Subject */}
          <p 
            className={cn(
              'text-sm truncate mb-1',
              !message.isRead && 'font-semibold'
            )}
            style={{ color: 'var(--text-primary)' }}
          >
            {message.subject}
          </p>
          
          {/* Preview or AI Summary — fixed min-height to prevent layout shift */}
          <div style={{ minHeight: '2.5rem' }}>
            {summary ? (
              <div 
                className="flex items-start gap-1.5 text-xs break-all"
                style={{ color: 'var(--text-secondary)' }}
              >
                <Brain 
                  className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" 
                  style={{ color: 'rgb(168, 85, 247)' }} 
                />
                <span className="italic">{summary}</span>
              </div>
            ) : (
              <p 
                className="text-xs break-all"
                style={{ color: 'var(--text-tertiary)' }}
              >
                {message.bodyPreview}
              </p>
            )}
          </div>
          
          {/* Meta badges */}
          <div className="flex items-center gap-2 mt-2">
            {message.hasAttachments && (
              <Paperclip className="w-3 h-3" style={{ color: 'var(--text-tertiary)' }} />
            )}
            {message.importance === 'high' && (
              <Star className="w-3 h-3 text-warning fill-warning" />
            )}
            {/* Todo Link - Clickable checkbox to mark as done */}
            {todoLink ? (
              <button
                onClick={handleToggleTodo}
                disabled={toggleTodoMutation.isPending}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium transition-all hover:scale-105"
                style={{
                  backgroundColor: todoLink.completed 
                    ? 'rgba(16, 185, 129, 0.15)' 
                    : 'rgba(234, 179, 8, 0.15)',
                  color: todoLink.completed 
                    ? 'var(--color-success)' 
                    : 'var(--color-warning)',
                  cursor: toggleTodoMutation.isPending ? 'wait' : 'pointer',
                }}
                title={todoLink.completed ? 'Als unerledigt markieren' : 'Als erledigt markieren (klicken)'}
              >
                {toggleTodoMutation.isPending ? (
                  <Loader2 className="w-2.5 h-2.5 animate-spin" />
                ) : todoLink.completed ? (
                  <CheckCircle className="w-2.5 h-2.5" />
                ) : (
                  <Circle className="w-2.5 h-2.5" />
                )}
                {todoLink.completed ? '✓ Erledigt' : 'Offen'}
              </button>
            ) : onAddTodo && (
              <button
                onClick={handleAddTodo}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium transition-all hover:scale-105"
                style={{
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  color: 'var(--brand-primary)',
                  border: '1px dashed var(--brand-primary)',
                }}
                title="Aufgabe aus E-Mail erstellen"
              >
                <Plus className="w-2.5 h-2.5" />
                Aufgabe
              </button>
            )}
            {message.categories?.slice(0, 2).map((cat: string, i: number) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                style={{
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  color: 'var(--brand-primary)',
                }}
              >
                {cat}
              </span>
            ))}
          </div>
          
          {/* Tasks toggle button - only show when To-Do link exists */}
          {todoLink && (
            <button
              onClick={handleToggleTasks}
              className="flex items-center gap-1 mt-2 text-xs transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--brand-primary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
            >
              {showTasks ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  <span>Subtasks ausblenden</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  <span>Subtasks anzeigen</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* Collapsible Microsoft To-Do Subtasks Section */}
      {showTasks && todoLink && (
        <div 
          className="mt-3 ml-1 pl-3 py-2 rounded-r-lg"
          style={{ 
            borderLeft: '2px solid var(--brand-primary)',
            backgroundColor: 'var(--bg-secondary)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <SubtaskList
            accountId={accountId}
            messageId={message.id}
            provider={provider}
            todoLinkExists={!!todoLink}
          />
        </div>
      )}
    </div>
  );
}
